'use client';

import React from 'react';
import { Lightbulb, Radio, Dna, ArrowRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';



interface Props {
  onNext: () => void;
}

const facts = [
  {
    id: 'fact-infrared',
    icon: Lightbulb,
    number: '01',
    title: 'Your Body Emits Infrared Light Energy',
    description:
      'Every cell in your body continuously emits and receives photons — tiny packets of light energy. This biophoton communication system regulates cellular function, repair, and regeneration.',
    stat: '10+ years',
    statLabel: 'of phototherapy research',
  },
  {
    id: 'fact-reflect',
    icon: Radio,
    number: '02',
    title: 'X39 Reflects Specific Wavelengths Back to Your Skin',
    description:
      'The X39 patch contains a patented photoreflective substrate that captures your body\'s own infrared output and reflects specific wavelengths back — acting as a signal amplifier for your own biology.',
    stat: '0 drugs',
    statLabel: 'nothing enters the body',
  },
  {
    id: 'fact-ghk',
    icon: Dna,
    number: '03',
    title: 'Activates GHK-Cu — Linked to Stem Cell Activation',
    description:
      'Clinical studies show X39 elevates levels of GHK-Cu, a naturally occurring copper peptide associated with stem cell activation, wound healing, anti-aging, and tissue regeneration.',
    stat: '80+ studies',
    statLabel: 'on GHK-Cu peptide',
  },
];

export default function Slide3Science({ onNext }: Props) {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 40% at 50% 0%, rgba(0,229,255,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-5xl w-full mx-auto fade-in">
        <div className="text-center mb-12">
          <p className="text-accent text-sm font-semibold tracking-widest uppercase mb-3">
            The Science
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            Phototherapy:{' '}
            <span className="gradient-text">10 Years of Research,</span>
            <br />
            Now in a Patch
          </h2>
          <p className="text-secondary-foreground text-lg max-w-2xl mx-auto">
            2025 Stem Cell Innovation of the Year meets wearable technology. Your body already
            knows how to heal — X39 gives it the signal.
          </p>
        </div>

        <div className="space-y-5 mb-12">
          {facts.map((fact, idx) => {
            const Icon = fact.icon;
            return (
              <React.Fragment key={fact.id}>
                <div
                  className="glass-card p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start"
                  style={{ animationDelay: `${idx * 0.15}s` }}
                >
                  <div className="flex-shrink-0 flex items-center gap-4">
                    <span className="text-5xl font-extrabold text-primary/20 leading-none tabular-nums">
                      {fact.number}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Icon size={22} className="text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-foreground font-bold text-xl mb-2">{fact.title}</h3>
                    <p className="text-secondary-foreground text-sm leading-relaxed mb-4">
                      {fact.description}
                    </p>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
                      <span className="gradient-text font-bold text-sm">{fact.stat}</span>
                      <span className="text-muted-foreground text-xs">{fact.statLabel}</span>
                    </div>
                  </div>
                </div>
                {fact.number === '03' && (
                  <div className="glass-card p-4 sm:p-6">
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        title="vimeo-player"
                        src="https://player.vimeo.com/video/1067982404?h=13d2c85789"
                        className="absolute inset-0 w-full h-full rounded-lg"
                        frameBorder="0"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Award Badge — after video */}
        <div className="flex justify-center mb-8">
          <div className="glass-card px-5 py-4 inline-flex items-center gap-4 border-primary/20 bg-primary/5">
            <img
              src="/assets/images/image-1789382929500.png"
              alt="2025 Biotech Breakthrough Award"
              className="h-16 w-auto object-contain"
            />
            <div className="text-left">
              <p className="text-primary text-xs font-semibold tracking-widest uppercase">Recognized</p>
              <p className="text-foreground font-bold text-sm">2025 Biotech Breakthrough Award</p>
            </div>
          </div>
        </div>

        {/* Animated wave visual */}
        <div className="flex justify-center mb-8">
          <div className="flex items-end gap-1 h-12">
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={`bar-${i}`}
                className="w-1.5 rounded-full bg-primary/60"
                style={{
                  height: `${20 + Math.sin(i * 0.5) * 18}px`,
                  opacity: 0.4 + Math.abs(Math.sin(i * 0.4)) * 0.6,
                  animation: `float ${1.5 + (i % 4) * 0.3}s ease-in-out ${i * 0.05}s infinite`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="text-center">
          <button onClick={onNext} className="btn-primary inline-flex items-center gap-2">
            See the Results
            <ArrowRight size={18} />
          </button>
          <p className="text-muted-foreground text-xs mt-4 max-w-xl mx-auto">
            This product is not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </div>
    </div>
  );
}